/**
 * Etiket sayfalarını PDF olarak indirir.
 *
 * Sayfalar `labelCanvasRenderer` ile doğrudan canvas'a çizilip PDF'e görüntü
 * olarak gömülür. jsPDF'in kendi metin API'si WinAnsi kodlaması yaptığı ve
 * ş/ğ/İ/ı karakterlerini içermediği için PDF metni yazmıyoruz.
 *
 * Önceki sürüm etiketleri DOM'da kurup html2canvas ile yakalıyordu; ölçümde
 * 100 sayfalık bir iş 114 saniye sürüyordu (çağrı başına ~4 sn sabit maliyet).
 * Gerekçe ve ölçümler için bkz. labelCanvasRenderer başlığı.
 *
    10| * Not: keskin çıktı için asıl yol tarayıcı yazdırmadır (metin vektör kalır).
 * PDF arşiv ve paylaşım amaçlıdır.
 */

import jsPDF from 'jspdf';
import { LabelTemplate } from '../../../types/labelTemplate.types';
import { LabelPage } from './labelImposition';
import { PrintSheetSize } from './printViaBrowser';
import { drawLabel, preloadLabelAssets } from '../render/labelCanvasRenderer';
import { yieldToBrowser } from './hiddenRenderHost';
import { createPerfAccumulator, perfLog } from './labelPerf';

export interface LabelPdfOptions {
  fileName?: string;
  /** Hedef çözünürlük. Yükseldikçe keskinleşir, bellek ve süre artar. */
  dpi?: number;
  /** İndirmek yerine yeni sekmede aç */
  openInNewTab?: boolean;
  /** Sayfa sayfa ilerleme bildirimi */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Sayfa görüntüsünün JPEG kalitesi.
 *
 * PNG ile sayfa gömme ölçümde 213 ms/sayfa sürüyordu (100 sayfa = 21 sn):
 * zlib sıkıştırması pahalı ve jsPDF gömerken PNG'yi yeniden işliyor. JPEG
 * kodlama kat kat hızlı ve jsPDF onu olduğu gibi (DCTDecode) gömüyor.
 *
 * 0.95: etiket içeriği keskin siyah-beyaz olduğu için JPEG'in zayıf olduğu
 * yer tam burası; kaliteyi yükseltmek kenar pürüzünü gözle görülmez yapıyor,
 * dosya boyutu artışı etiket ölçüsünde önemsiz.
 */
const JPEG_QUALITY = 0.95;

/**
 * Sayfa alanına göre çözünürlük seçer.
 *
 * A4 sayfa 300 dpi'de 8,7 megapiksel ediyor ve kodlama maliyeti alanla doğru
 * orantılı. Rulo etiketi (40x90 mm) aynı çözünürlükte yarım megapiksel —
 * orada kısmak gereksiz.
 */
function defaultDpi(sheet: PrintSheetSize): number {
  const areaMm2 = sheet.widthMm * sheet.heightMm;
  return areaMm2 > 30000 ? 200 : 300;
}

export async function exportLabelPagesToPdf(
  template: LabelTemplate,
  pages: LabelPage[],
  sheet: PrintSheetSize,
  options: LabelPdfOptions = {},
): Promise<void> {
  if (pages.length === 0) {
    throw new Error('Yazdırılacak sayfa yok');
  }

  const total = pages.length;
  const orientation = sheet.widthMm >= sheet.heightMm ? 'landscape' : 'portrait';
  const dpi = options.dpi ?? defaultDpi(sheet);
  const pxPerMm = dpi / 25.4;

  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    // Etiket ölçüsü standart bir formata karşılık gelmediği için açık ölçü
    format: [sheet.widthMm, sheet.heightMm],
  });

  options.onProgress?.(0, total);

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sheet.widthMm * pxPerMm));
  canvas.height = Math.max(1, Math.round(sheet.heightMm * pxPerMm));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D bağlamı alınamadı');

  const t0 = performance.now();
  const drawPerf = createPerfAccumulator('PDF · sayfa çizimi');
  const embedPerf = createPerfAccumulator('PDF · sayfa gömme (JPEG+jsPDF)');
  perfLog(
    `PDF başlıyor: ${total} sayfa, ${dpi} dpi, tuval ${canvas.width}x${canvas.height} px`,
  );

  // Görseller bir kez yüklenir; çizim senkron olmak zorunda
  await preloadLabelAssets(
    template,
    pages.flatMap((page) => page.labels.map((l) => l.instance.data)),
  );

  for (let i = 0; i < total; i++) {
    const page = pages[i];

    drawPerf.runSync(() => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (const placed of page.labels) {
        ctx.save();
        ctx.translate(placed.xMm * pxPerMm, placed.yMm * pxPerMm);
        drawLabel(ctx, template, placed.instance.data, { pxPerMm });
        ctx.restore();
      }
    });

    embedPerf.runSync(() => {
      if (i > 0) pdf.addPage([sheet.widthMm, sheet.heightMm], orientation);
      // Kodlama jsPDF'e bırakılmıyor: canvas verildiğinde kaliteyi kendisi
      // seçiyor ve JPEG_QUALITY'nin gerekçesi devre dışı kalıyor.
      const jpeg = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      pdf.addImage(jpeg, 'JPEG', 0, 0, sheet.widthMm, sheet.heightMm, undefined, 'FAST');
    });

    options.onProgress?.(i + 1, total);

    // İlerleme çubuğu ve iptal etkileşimi için arada tarayıcıya dön
    if (i % 5 === 4) await yieldToBrowser();
  }

  drawPerf.summary();
  embedPerf.summary();
  perfLog(`PDF toplam: ${((performance.now() - t0) / 1000).toFixed(1)} sn (${total} sayfa)`);

  // Tuvalin belleği hemen bırakılsın
  canvas.width = 0;
  canvas.height = 0;

  const fileName = options.fileName ?? `etiket-${new Date().toISOString().slice(0, 10)}.pdf`;

  if (options.openInNewTab) {
    const url = pdf.output('bloburl');
    window.open(url, '_blank');
    return;
  }

  pdf.save(fileName);
}
